/**
 * `/instagram-funnel` — the campaigns behind the Instagram comment-to-DM
 * funnel (migration 199).
 *
 * Reads and writes entirely through the chokepoint (`dispatchAction` →
 * `POST /api/dispatch/:action`, as `human:staff`), so the page owns NO bespoke
 * endpoint — `api: []` in page-registry, like `/assets` and `/errors`. That is
 * not only tidiness: every non-GET `/api` route has to be registered above the
 * GET-only guard in `server.ts` or it 405s silently, and a page that needs no
 * route cannot get that wrong.
 *
 * WHAT THIS PAGE IS FOR, and the copy that has to survive edits:
 *
 *   - A campaign is the ONLY thing in Layer that can message someone who never
 *     contacted the studio. So the page never hides what arming means: the
 *     Activate button has a confirm step that spells out, in words, which
 *     comments will match, how often one person can be reached, and whether a
 *     public reply gets posted.
 *
 *   - Campaigns are born `draft` and the create form cannot make an active
 *     one, because `create_instagram_funnel_campaign` takes no `status`. The
 *     form does not pretend otherwise with a disabled toggle.
 *
 *   - Delete is ABSENT, not merely refused, on any campaign with send history:
 *     the FK cascades and would take the cooldown ledger with it. The button
 *     is replaced by the reason, so the destructive control is not something
 *     an operator taps and argues with.
 *
 *   - `mode` IS EDITABLE since 2026-09-26, and only since then. It used to be
 *     display-only with this header saying why: the column existed and the router
 *     did not act on it, so a toggle changing a value nothing reads would be the
 *     most expensive kind of lie a config page can tell. The router now honours
 *     it, so the control appears — under three conditions that keep the same
 *     honesty:
 *
 *       1. `auto` is DISABLED until a post is nominated, with the reason shown
 *          rather than a refusal after the fact. Auto mode scoped to a keyword
 *          across the whole account would cold-DM anyone who wrote that word, and
 *          a burned comment can never be funnelled again.
 *       2. Choosing `auto` renders a warning IN THE FORM, not only at activation:
 *          the operator should know before typing the DM copy that nobody will
 *          read it before a stranger does.
 *       3. The public-reply toggle appears ONLY in `auto`, because in `approve`
 *          the reply already rides the approver's tap — a tick there would be
 *          asking about something that is not a choice.
 *
 *   - "Use for every new post" (migration 251, 2026-10-24) is a CARD action
 *     with a confirm step, like Activate, not a form field: it widens who gets
 *     messaged, the create action cannot take it (a draft must not hold the
 *     coverage), and the tick MOVES from the previous holder — which the confirm
 *     step names before it happens. The button is absent unless the campaign is
 *     active with a keyword and a post; a copy the funnel made says where it
 *     came from.
 *
 * All the decisions above are `lib/instagram-funnel-campaign.ts`, tested
 * without a DOM. This file is wiring and markup.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { dispatchAction } from "../lib/dispatch";
import {
  armingSummary,
  autoModeBlockedReason,
  buildCampaignPatch,
  buildCreateBody,
  campaignFormErrors,
  canCoverNewPosts,
  coverNewPostsSummary,
  coveringCampaign,
  deleteBlockedReason,
  describeMode,
  describeNewPostRole,
  describeSendCounts,
  describeTrigger,
  draftFromCampaign,
  emptyCampaignDraft,
  hasCampaignErrors,
  patchIsEmpty,
  CAMPAIGN_CHIP_LABEL_MAX,
  CAMPAIGN_DM_BODY_MAX,
  CAMPAIGN_PAYLOAD_BODY_MAX,
  type Campaign,
  type CampaignDraft,
  type CampaignMode,
  type CampaignStatus,
} from "../lib/instagram-funnel-campaign";

interface ListOutput {
  readonly campaigns: ReadonlyArray<Campaign>;
  readonly count_returned: number;
}

const STATUS_LABEL: Readonly<Record<CampaignStatus, string>> = {
  draft: "Draft",
  active: "Active",
  paused: "Paused",
};

/** Per-card transient state: which confirm step, if any, is open. */
type CardMode =
  | { readonly kind: "idle" }
  | { readonly kind: "confirm-activate" }
  | { readonly kind: "confirm-cover" }
  | { readonly kind: "confirm-delete" }
  | { readonly kind: "editing"; readonly draft: CampaignDraft };

export function InstagramFunnel(): JSX.Element {
  const [campaigns, setCampaigns] = useState<ReadonlyArray<Campaign>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cardMode, setCardMode] = useState<Record<string, CardMode>>({});
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<CampaignDraft>(emptyCampaignDraft());

  const load = useCallback(async () => {
    setLoading(true);
    const r = await dispatchAction<Record<string, never>, ListOutput>(
      "list_instagram_funnel_campaigns",
      {} as Record<string, never>,
    );
    if (r.ok) {
      setCampaigns(r.response.campaigns);
      setLoadError(null);
    } else {
      setLoadError(`${r.code}: ${r.message}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const modeOf = (id: string): CardMode => cardMode[id] ?? { kind: "idle" };
  const setMode = (id: string, m: CardMode): void =>
    setCardMode((prev) => ({ ...prev, [id]: m }));

  /** One place for "dispatch, report the code, reload" — every mutation uses it. */
  const run = useCallback(
    async (id: string, action: string, input: unknown): Promise<boolean> => {
      setBusyId(id);
      setActionError(null);
      const r = await dispatchAction<unknown, unknown>(action, input);
      setBusyId(null);
      if (!r.ok) {
        // The action's stable code, verbatim. `instagram_funnel_campaign_has_-
        // sends` and `…_needs_trigger` are written to be read by a person.
        setActionError(`${r.code}: ${r.message}`);
        return false;
      }
      setMode(id, { kind: "idle" });
      await load();
      return true;
    },
    [load],
  );

  const createErrors = useMemo(() => campaignFormErrors(createDraft), [createDraft]);

  const onCreate = useCallback(async () => {
    if (hasCampaignErrors(createErrors)) return;
    const ok = await run("new", "create_instagram_funnel_campaign", buildCreateBody(createDraft));
    if (ok) {
      setCreateDraft(emptyCampaignDraft());
      setCreating(false);
    }
  }, [createDraft, createErrors, run]);

  return (
    <div>
      <h1 className="page-title">Instagram funnel</h1>

      <p className="ifc-intro">
        Someone comments on a Reel, gets a DM with a button, and the tap delivers
        the payload. A campaign decides which comments match and what they get.
        This is the only thing in Clavis that messages people who have never
        contacted the studio, so a campaign starts as a draft and does nothing
        until you activate it — and even then, in approve mode every DM waits for
        your approval in the <Link to="/inbox">inbox</Link>, while auto mode sends
        it straight away.
      </p>

      {loadError !== null ? (
        <div className="ifc-error" role="alert">
          Could not load campaigns — {loadError}
        </div>
      ) : null}
      {actionError !== null ? (
        <div className="ifc-error" role="alert">
          {actionError}
        </div>
      ) : null}

      <div className="ifc-toolbar">
        <button
          type="button"
          className="dc-btn"
          onClick={() => setCreating((c) => !c)}
        >
          {creating ? "Cancel" : "New campaign"}
        </button>
        <button type="button" className="dc-btn" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {creating ? (
        <section className="panel ifc-form">
          <h2>New campaign</h2>
          <p className="ifc-note">
            Created as a <strong>draft</strong>. Nothing matches or sends until
            you activate it.
          </p>
          <CampaignFields
            draft={createDraft}
            errors={createErrors}
            onChange={setCreateDraft}
          />
          <div className="ifc-actions">
            <button
              type="button"
              className="dc-btn"
              disabled={hasCampaignErrors(createErrors) || busyId === "new"}
              onClick={() => void onCreate()}
            >
              {busyId === "new" ? "Creating…" : "Create draft"}
            </button>
          </div>
        </section>
      ) : null}

      {loading ? <p className="empty">Loading…</p> : null}
      {!loading && campaigns.length === 0 ? (
        <p className="empty">No campaigns yet.</p>
      ) : null}

      {campaigns.map((c) => {
        const mode = modeOf(c.id);
        const blocked = deleteBlockedReason(c);
        const busy = busyId === c.id;
        const newPostRole = describeNewPostRole(c, campaigns);
        return (
          <section
            key={c.id}
            className={`panel ifc-card ifc-card-${c.status}`}
          >
            <div className="ifc-card-head">
              <h2>{c.name}</h2>
              <span className={`ifc-pill ifc-pill-${c.status}`}>
                {STATUS_LABEL[c.status]}
              </span>
            </div>

            <dl className="ifc-facts">
              <div>
                <dt>Matches</dt>
                <dd>{describeTrigger(c)}</dd>
              </div>
              <div>
                <dt>Cooldown</dt>
                <dd>
                  {c.cooldown_days} day{c.cooldown_days === 1 ? "" : "s"} per person
                </dd>
              </div>
              <div>
                <dt>Mode</dt>
                <dd>{describeMode(c.mode, c.auto_public_reply)}</dd>
              </div>
              <div>
                <dt>So far</dt>
                <dd>{describeSendCounts(c.send_counts)}</dd>
              </div>
              {newPostRole !== null ? (
                <div>
                  <dt>New posts</dt>
                  <dd>{newPostRole}</dd>
                </div>
              ) : null}
            </dl>

            {mode.kind === "editing" ? (
              <EditBlock
                campaign={c}
                draft={mode.draft}
                busy={busy}
                onChange={(d) => setMode(c.id, { kind: "editing", draft: d })}
                onCancel={() => setMode(c.id, { kind: "idle" })}
                onSave={(patch) =>
                  void run(c.id, "update_instagram_funnel_campaign", {
                    campaign_id: c.id,
                    patch,
                  })
                }
              />
            ) : (
              <div className="ifc-bodies">
                <p>
                  <span className="ifc-label">DM</span> {c.dm_body}
                </p>
                <p>
                  <span className="ifc-label">Button</span> {c.chip_label}
                </p>
                <p>
                  <span className="ifc-label">Delivers</span> {c.payload_body}
                  {c.payload_url !== null ? ` — ${c.payload_url}` : ""}
                </p>
                {c.public_reply_body !== null ? (
                  <p>
                    <span className="ifc-label">Public reply</span>{" "}
                    {c.public_reply_body}
                  </p>
                ) : null}
              </div>
            )}

            {mode.kind === "confirm-activate" ? (
              <div className="ifc-confirm" role="alert">
                <p className="ifc-confirm-title">Activate {c.name}?</p>
                <ul>
                  {armingSummary(c).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="ifc-actions">
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(c.id, "update_instagram_funnel_campaign", {
                        campaign_id: c.id,
                        patch: { status: "active" },
                      })
                    }
                  >
                    {busy ? "Activating…" : "Yes, activate"}
                  </button>
                  <button
                    type="button"
                    className="dc-btn"
                    onClick={() => setMode(c.id, { kind: "idle" })}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {mode.kind === "confirm-cover" ? (
              <div className="ifc-confirm" role="alert">
                <p className="ifc-confirm-title">Use {c.name} for every new post?</p>
                <ul>
                  {coverNewPostsSummary(c, coveringCampaign(campaigns)).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="ifc-actions">
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(c.id, "update_instagram_funnel_campaign", {
                        campaign_id: c.id,
                        patch: { covers_new_posts: true },
                      })
                    }
                  >
                    {busy ? "Saving…" : "Yes, use it for new posts"}
                  </button>
                  <button
                    type="button"
                    className="dc-btn"
                    onClick={() => setMode(c.id, { kind: "idle" })}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {mode.kind === "confirm-delete" ? (
              <div className="ifc-confirm" role="alert">
                <p className="ifc-confirm-title">Delete {c.name}?</p>
                <p>
                  It has never staged anything, so there is nothing to lose. This
                  cannot be undone.
                </p>
                <div className="ifc-actions">
                  <button
                    type="button"
                    className="dc-btn dc-btn-danger"
                    disabled={busy}
                    onClick={() =>
                      void run(c.id, "delete_instagram_funnel_campaign", {
                        campaign_id: c.id,
                      })
                    }
                  >
                    {busy ? "Deleting…" : "Yes, delete"}
                  </button>
                  <button
                    type="button"
                    className="dc-btn"
                    onClick={() => setMode(c.id, { kind: "idle" })}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {mode.kind === "idle" ? (
              <div className="ifc-actions">
                {c.status === "active" ? (
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(c.id, "update_instagram_funnel_campaign", {
                        campaign_id: c.id,
                        patch: { status: "paused" },
                      })
                    }
                  >
                    Pause
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={busy}
                    onClick={() => setMode(c.id, { kind: "confirm-activate" })}
                  >
                    Activate
                  </button>
                )}
                {c.covers_new_posts ? (
                  // Stopping widens nothing, so — like Pause — it needs no confirm.
                  // The copies already made are campaigns of their own and stay.
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(c.id, "update_instagram_funnel_campaign", {
                        campaign_id: c.id,
                        patch: { covers_new_posts: false },
                      })
                    }
                  >
                    Stop using for new posts
                  </button>
                ) : canCoverNewPosts(c) ? (
                  <button
                    type="button"
                    className="dc-btn"
                    disabled={busy}
                    onClick={() => setMode(c.id, { kind: "confirm-cover" })}
                  >
                    Use for every new post
                  </button>
                ) : null}
                <button
                  type="button"
                  className="dc-btn"
                  disabled={busy}
                  onClick={() =>
                    setMode(c.id, { kind: "editing", draft: draftFromCampaign(c) })
                  }
                >
                  Edit
                </button>
                {blocked === null ? (
                  <button
                    type="button"
                    className="dc-btn dc-btn-danger"
                    disabled={busy}
                    onClick={() => setMode(c.id, { kind: "confirm-delete" })}
                  >
                    Delete
                  </button>
                ) : (
                  // NOT a disabled Delete button. A destructive control that is
                  // present and refuses invites a second try; the reason it is
                  // gone is more useful than the control.
                  <span className="ifc-blocked">{blocked}</span>
                )}
              </div>
            ) : null}
          </section>
        );
      })}

      <p className="ifc-footnote">
        Pausing stops staging without losing anything — comments keep arriving,
        ingesting and alerting. It is the stop to reach for; deleting is only
        possible for a campaign that never matched a comment, because the send
        rows a campaign owns are the cooldown ledger that stops the same person
        being messaged twice.
      </p>
      <p className="ifc-footnote">
        One active campaign with a keyword and its own post can be used for every
        new post. The first time someone comments its keyword on a post that has no
        campaign of its own, the campaign is copied onto that post and answers
        them; the copy then appears here like any other campaign. Only posts on
        this studio&rsquo;s own Instagram account are ever copied onto. An older
        post nobody set up counts too: to keep a post out, give it a campaign of
        its own &mdash; a paused one is enough. If a post that already has a
        copy is later given a campaign of its own, a comment matching both is
        answered by the post&rsquo;s own campaign.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function EditBlock(props: {
  readonly campaign: Campaign;
  readonly draft: CampaignDraft;
  readonly busy: boolean;
  readonly onChange: (d: CampaignDraft) => void;
  readonly onCancel: () => void;
  readonly onSave: (patch: Record<string, unknown>) => void;
}): JSX.Element {
  const errors = campaignFormErrors(props.draft, props.campaign.covers_new_posts);
  const patch = buildCampaignPatch(props.campaign, props.draft);
  const nothingToSave = patchIsEmpty(patch);
  return (
    <div className="ifc-edit">
      <CampaignFields draft={props.draft} errors={errors} onChange={props.onChange} />
      <div className="ifc-actions">
        <button
          type="button"
          className="dc-btn"
          disabled={hasCampaignErrors(errors) || nothingToSave || props.busy}
          onClick={() => props.onSave(patch as Record<string, unknown>)}
        >
          {props.busy ? "Saving…" : nothingToSave ? "No changes" : "Save"}
        </button>
        <button type="button" className="dc-btn" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
      <p className="ifc-note">
        Editing a campaign changes what FUTURE comments get. A DM already staged
        in the inbox keeps the copy it was staged with.
      </p>
    </div>
  );
}

/**
 * The form fields.
 *
 * A field's error appears only once that field has been TOUCHED. An empty new
 * campaign is invalid in five ways, and rendering all five in red before the
 * operator has typed a character reads as five things going wrong rather than
 * as a blank form. The rule that matters — a campaign needs a trigger — is
 * stated up front as the fieldset's legend, where it is guidance instead of an
 * accusation, and becomes an error once someone has actually been in the field.
 *
 * The Save/Create button still disables on the FULL error set, touched or not,
 * so nothing invalid can be submitted by not looking at it.
 */
function CampaignFields(props: {
  readonly draft: CampaignDraft;
  readonly errors: ReturnType<typeof campaignFormErrors>;
  readonly onChange: (d: CampaignDraft) => void;
}): JSX.Element {
  const { draft, errors, onChange } = props;
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const touch = (k: keyof CampaignDraft): void =>
    setTouched((prev) => (prev.has(k) ? prev : new Set([...prev, k])));
  const errorFor = (k: keyof CampaignDraft): string | undefined =>
    touched.has(k) ? errors[k] : undefined;
  const set = (k: keyof CampaignDraft, v: string): void => {
    touch(k);
    onChange({ ...draft, [k]: v });
  };
  // The two non-text controls (2026-09-26): a select and a checkbox. Separate
  // setters rather than widening `set`, so the text path keeps its string type.
  const setMode = (v: CampaignMode): void => {
    touch("mode");
    // Switching AWAY from auto disarms the public reply, because the toggle is
    // only meaningful in auto and leaving it armed would present an operator with
    // a tick that does nothing. Switching INTO auto never arms anything.
    onChange({
      ...draft,
      mode: v,
      ...(v === "approve" ? { auto_public_reply: false } : {}),
    });
  };
  const setAutoPublicReply = (v: boolean): void => {
    touch("auto_public_reply");
    onChange({ ...draft, auto_public_reply: v });
  };
  const autoBlocked = autoModeBlockedReason(draft);
  return (
    <div className="ifc-fields">
      <Field label="Name" error={errorFor("name")}>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => set("name", e.target.value)}
          onBlur={() => touch("name")}
        />
      </Field>

      <fieldset className="ifc-fieldset">
        <legend>
          Trigger — <strong>at least one</strong>. A campaign with neither would DM
          everyone who comments. Both set means that word, on that post.
        </legend>
        <Field label="Keyword in the comment" error={errorFor("trigger_keyword")}>
          <input
            type="text"
            value={draft.trigger_keyword}
            onChange={(e) => set("trigger_keyword", e.target.value)}
          onBlur={() => touch("trigger_keyword")}
          />
        </Field>
        <Field label="Only on this post (media id)" error={errorFor("trigger_media_id")}>
          <input
            type="text"
            value={draft.trigger_media_id}
            onChange={(e) => set("trigger_media_id", e.target.value)}
          onBlur={() => touch("trigger_media_id")}
          />
        </Field>
      </fieldset>

      {/*
        SENDING MODE (2026-09-26). Deliberately its own fieldset directly under
        the trigger, because the two are coupled: auto mode REQUIRES a nominated
        post, so the control is disabled with its reason until one is set rather
        than offered and then refused by the server.
      */}
      <fieldset className="ifc-fieldset">
        <legend>
          Sending — <strong>approve</strong> stages every match for a human tap;{" "}
          <strong>auto</strong> sends it immediately, with nobody in the loop.
        </legend>
        <Field label="Mode" error={errorFor("mode")}>
          <select
            value={draft.mode}
            onChange={(e) => setMode(e.target.value as CampaignMode)}
          >
            <option value="approve">approve — a human taps before anything sends</option>
            <option value="auto" disabled={autoBlocked !== null}>
              auto — sends the DM with no human step
            </option>
          </select>
        </Field>
        {autoBlocked !== null ? (
          <p className="ifc-note">{autoBlocked}</p>
        ) : null}
        {draft.mode === "auto" ? (
          <p className="ifc-warning">
            ⚠️ Comments matching this trigger will be DMed with no human step.
            Instagram allows one private reply per comment, ever, so a send cannot
            be undone or repeated — and a comment this campaign burns can never be
            funnelled again.
          </p>
        ) : null}
      </fieldset>

      <Field
        label={`The DM (${draft.dm_body.trim().length}/${CAMPAIGN_DM_BODY_MAX})`}
        error={errorFor("dm_body")}
      >
        <textarea
          rows={3}
          value={draft.dm_body}
          onChange={(e) => set("dm_body", e.target.value)}
          onBlur={() => touch("dm_body")}
        />
      </Field>

      <Field
        label={`Button label (${draft.chip_label.trim().length}/${CAMPAIGN_CHIP_LABEL_MAX})`}
        error={errorFor("chip_label")}
      >
        <input
          type="text"
          value={draft.chip_label}
          onChange={(e) => set("chip_label", e.target.value)}
          onBlur={() => touch("chip_label")}
        />
      </Field>

      <Field
        label={`What the tap delivers (${draft.payload_body.trim().length}/${CAMPAIGN_PAYLOAD_BODY_MAX})`}
        error={errorFor("payload_body")}
      >
        <textarea
          rows={3}
          value={draft.payload_body}
          onChange={(e) => set("payload_body", e.target.value)}
          onBlur={() => touch("payload_body")}
        />
      </Field>

      <Field label="Link to include (optional, https)" error={errorFor("payload_url")}>
        <input
          type="text"
          value={draft.payload_url}
          onChange={(e) => set("payload_url", e.target.value)}
          onBlur={() => touch("payload_url")}
        />
      </Field>

      <Field
        label="Public comment reply (optional — leave blank to post nothing)"
        error={errorFor("public_reply_body")}
      >
        <textarea
          rows={2}
          value={draft.public_reply_body}
          onChange={(e) => set("public_reply_body", e.target.value)}
          onBlur={() => touch("public_reply_body")}
        />
      </Field>

      {/*
        The public reply's OWN opt-in, shown only in auto mode — in approve mode
        the reply already rides the approver's tap, so a toggle there would be
        asking about something that is not a choice. Off by default, and separate
        from the mode because posting under the studio's own post is PUBLIC,
        PERMANENT and bounded by no window at all, unlike the DM.
      */}
      {draft.mode === "auto" ? (
        <label className="ifc-check">
          <input
            type="checkbox"
            checked={draft.auto_public_reply}
            onChange={(e) => setAutoPublicReply(e.target.checked)}
          />
          <span>
            Also post that public reply automatically
            {errorFor("auto_public_reply") !== undefined ? (
              <span className="ifc-field-error">{errorFor("auto_public_reply")}</span>
            ) : null}
          </span>
        </label>
      ) : null}

      <Field label="Cooldown, days per person" error={errorFor("cooldown_days")}>
        <input
          type="number"
          value={draft.cooldown_days}
          onChange={(e) => set("cooldown_days", e.target.value)}
          onBlur={() => touch("cooldown_days")}
        />
      </Field>
    </div>
  );
}

function Field(props: {
  readonly label: string;
  readonly error?: string | undefined;
  readonly children: JSX.Element;
}): JSX.Element {
  return (
    <label className="ifc-field">
      <span className="ifc-field-label">{props.label}</span>
      {props.children}
      {props.error !== undefined ? (
        <span className="ifc-field-error">{props.error}</span>
      ) : null}
    </label>
  );
}
